import { HttpParams } from '@angular/common/http';
import { Component, Inject, TemplateRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { EMPTY, from, of } from 'rxjs';
import { catchError, concatMap, debounceTime, finalize, map, switchMap, tap } from 'rxjs/operators';
import { SitesService } from 'src/app/mobile/shared/sites.service';
import { AccountsService } from 'src/app/parameters/accounts/accounts.service';
import { ConfirmDialogComponent } from 'src/app/shared/components/confirm-dialog/confirm-dialog.component';
import { LinkGroupModalComponent } from 'src/app/shared/components/link-group-modal/link-group-modal.component';
import { Choice } from 'src/app/shared/components/select/choice.model';
import { BasicService } from 'src/app/shared/services/basic.service';

import { AddAccountService } from '../../add-account.service';
import { GoogleBusinessProfileService } from '../google-business-profile.service';
import { ConnectionStatus } from '../shared/connection-status.enum';
import { GoogleBusinessProfileOpenInformationEnum } from '../shared/google-business-profile-open-information.enum';

@Component({
  selector: 'app-google-business-profile-locations',
  templateUrl: './google-business-profile-locations.component.html',
  styleUrls: ['./google-business-profile-locations.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class GoogleBusinessProfileLocationsComponent {
  accountName: string;
  selectedLocationIds: Set<number> = new Set();
  connectedLocationsIds: Set<number> = new Set();
  locations = [];
  locationsFiltered = [];
  displayedColumns: string[] = ['selection', 'storeCode', 'name', 'address', 'open-info'];
  dataSource = new MatTableDataSource([]);
  connections: { [key: string]: { status: string; checked: boolean } } = {};
  loading: boolean;
  errors: { [key: string]: string } = {};
  selectedError: string;

  operatorsChoicesList: Choice[] = [
    { id: 0, label: this.basicService.translate('add-account/gmb/locations/filters/operators/storeCode/equals') },
    { id: 1, label: this.basicService.translate('add-account/gmb/locations/filters/operators/name/equals') },
    { id: 2, label: this.basicService.translate('add-account/gmb/locations/filters/operators/name/contains') },
    { id: 3, label: this.basicService.translate('add-account/gmb/locations/filters/operators/name/starts-with') },
    { id: 4, label: this.basicService.translate('add-account/gmb/locations/filters/operators/name/ends-with') },
    { id: 5, label: this.basicService.translate('add-account/gmb/locations/filters/operators/address/equals') },
    { id: 6, label: this.basicService.translate('add-account/gmb/locations/filters/operators/address/contains') },
    { id: 7, label: this.basicService.translate('add-account/gmb/locations/filters/operators/address/starts-with') },
    { id: 8, label: this.basicService.translate('add-account/gmb/locations/filters/operators/address/ends-with') },
  ];

  statusesChoicesList: Choice[] = [
    { id: 'connected', label: this.basicService.translate('add-account/gmb/locations/status/connected') },
    { id: 'not-connected', label: this.basicService.translate('add-account/gmb/locations/status/not-connected') },
  ];

  openInformationChoicesList: Choice[] = [
    {
      id: GoogleBusinessProfileOpenInformationEnum.OPEN,
      label: this.basicService.translate('add-account/gmb/location/open-info/open'),
    },
    {
      id: GoogleBusinessProfileOpenInformationEnum.CLOSED_TEMPORARILY,
      label: this.basicService.translate('add-account/gmb/location/open-info/closed-temporarily'),
    },
    {
      id: GoogleBusinessProfileOpenInformationEnum.CLOSED_PERMANENTLY,
      label: this.basicService.translate('add-account/gmb/location/open-info/closed-permanently'),
    },
    {
      id: GoogleBusinessProfileOpenInformationEnum.OPEN_FOR_BUSINESS_UNSPECIFIED,
      label: this.basicService.translate('add-account/gmb/location/open-info/not-specified'),
    },
  ];

  filtersGroup = new FormGroup({
    search: new FormControl(''),
    operator: new FormControl(this.operatorsChoicesList[0].id),
    statuses: new FormControl([]),
    openInformation: new FormControl([]),
  });

  readonly MAX_CONNECTIONS_ALLOWED = 1000;
  readonly GMB_SITE_TYPE = 11;

  ConnectionStatus = ConnectionStatus;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild('errorDetailTemplateRef') errorDetailTemplateRef: TemplateRef<any>;

  constructor(
    private basicService: BasicService,
    private addAccountService: AddAccountService,
    private accountsService: AccountsService,
    private dialog: MatDialog,
    private googleBusinessProfileService: GoogleBusinessProfileService,
    private sitesService: SitesService,

    @Inject('AlertingFactory') private alertingService: any,
    @Inject('$state') private $state: any,
    @Inject('$stateParams') private $stateParams: any
  ) {
    const { companyId, requestId, siteId, code, accountId } = $stateParams;
    this.basicService.showSpinner();
    googleBusinessProfileService.getAccounts(companyId, requestId, siteId, code).subscribe({
      next: (accounts: any[]) => {
        this.accountName = accounts.find(account => account.id === accountId).name;
        this.getData();
      },
    });
    this.filtersGroup.valueChanges.pipe(debounceTime(300)).subscribe(value => (this.dataSource.filter = value));
  }

  getData() {
    const { requestId, accountId } = this.$stateParams;
    let params = new HttpParams();
    params = params.append('request_id', requestId || null);
    params = params.append('account_id', accountId);

    //Get locations for alcmeon backend
    this.addAccountService
      .getGmbLocations(params)
      .pipe(finalize(() => this.basicService.hideSpinner()))
      .subscribe({
        next: (response: any) => {
          if (response.reauth) {
            window.location.href = response.redirect_url;
          } else if (response.error) {
            const url = `${response.redirect_url}?error=${response.reason}`;
            window.location.href = url;
          } else {
            this.locations = response.locations.map(location => {
              if (location) {
                const { address, postalCode, locality, country, id, is_connected } = location;
                const addressLabel = [address, postalCode, locality, country].filter(value => value).join(', ');
                this.connections[id] = {
                  status: is_connected ? ConnectionStatus.CONNECTED : ConnectionStatus.NOT_CONNECTED,
                  checked: is_connected,
                };
                return { ...location, addressLabel };
              }
            });
            this.locationsFiltered = [].concat(this.locations);
            this.connectedLocationsIds = new Set(
              this.locationsFiltered.filter(location => location?.is_connected).map(({ id }) => id)
            );
            this.dataSource = new MatTableDataSource(this.locationsFiltered);
            this.dataSource.paginator = this.paginator;

            this.dataSource.filterPredicate = (data, filter) => {
              if (!filter) {
                return true;
              }
              const { search, statuses, openInformation } = filter as any;
              const hasFilter = !!(search || statuses?.length);
              const storeCodeLowercase = (data.storeCode || '').toLowerCase();
              const nameLowercase = (data.name || '').toLowerCase();
              const addressLabelLowercase = (data.addressLabel || '').toLowerCase();
              const filterLowercase = (search || '').toLowerCase();
              let searchMatched = false;
              switch (this.filtersGroup.value.operator) {
                case 0:
                  searchMatched = storeCodeLowercase === filterLowercase;
                  break;
                case 1:
                  searchMatched = nameLowercase === filterLowercase;
                  break;
                case 2:
                  searchMatched = nameLowercase.indexOf(filterLowercase) > -1;
                  break;
                case 3:
                  searchMatched = nameLowercase.startsWith(filterLowercase);
                  break;
                case 4:
                  searchMatched = nameLowercase.endsWith(filterLowercase);
                  break;
                case 5:
                  searchMatched = addressLabelLowercase === filterLowercase;
                  break;
                case 6:
                  searchMatched = addressLabelLowercase.indexOf(filterLowercase) > -1;
                  break;
                case 7:
                  searchMatched = addressLabelLowercase.startsWith(filterLowercase);
                  break;
                case 8:
                  searchMatched = addressLabelLowercase.endsWith(filterLowercase);
                  break;
                default:
                  break;
              }
              return (
                !hasFilter ||
                ((!search || searchMatched) &&
                  (!statuses || !statuses.length || statuses.includes(this.connections[data.id]?.status)) &&
                  (!openInformation ||
                    !openInformation.length ||
                    !data.open_info ||
                    openInformation.includes(data.open_info)))
              );
            };

            this.filtersGroup.patchValue({
              statuses: ['not-connected'],
              openInformation: [
                GoogleBusinessProfileOpenInformationEnum.OPEN,
                GoogleBusinessProfileOpenInformationEnum.CLOSED_TEMPORARILY,
                GoogleBusinessProfileOpenInformationEnum.OPEN_FOR_BUSINESS_UNSPECIFIED,
              ],
            });
          }
        },
        error: response => {
          this.alertingService.alertHTTPError(response);
        },
      });
  }

  goBack() {
    this.$state.go('auth.alcmeon.parameters.gmb.add-account');
  }

  selectFilteredLocations() {
    const firstSelected = this.dataSource.filteredData
      .filter(
        ({ id, is_connected }) => (!is_connected && this.connections[id].checked) || !this.connections[id].checked
      )
      .slice(0, this.MAX_CONNECTIONS_ALLOWED);

    for (let index = 0; index < firstSelected.length; index++) {
      const element = firstSelected[index];
      if (this.selectedLocationIds.size >= this.MAX_CONNECTIONS_ALLOWED) {
        break;
      }
      this.connections[element.id].checked = true;
      this.selectedLocationIds.add(element.id);
    }
  }

  unselectFilteredLocations() {
    this.dataSource.filteredData
      .filter(({ id }) => {
        this.selectedLocationIds.delete(id);
        return this.connections[id].checked && !this.connectedLocationsIds.has(id);
      })
      .forEach(({ id }) => (this.connections[id].checked = false));
  }

  canAddLocations() {
    return this.selectedLocationIds.size > 0;
  }

  onLocationClick(checked: boolean, id: number) {
    if (checked) {
      this.selectedLocationIds.add(id);
    } else {
      this.selectedLocationIds.delete(id);
    }
    this.connections[id].checked = checked;
  }

  shouldDisableLocation(location) {
    return (
      location.is_connected ||
      (!this.selectedLocationIds.has(location.id) && this.selectedLocationIds.size >= this.MAX_CONNECTIONS_ALLOWED)
    );
  }

  finalizeAuthorization() {
    this.loading = true;
    const { companyId } = this.$stateParams;

    const allGmbSiteGroups = this.sitesService.siteGroupsV2.filter(sg => sg.type === this.GMB_SITE_TYPE);
    const siteGroups = allGmbSiteGroups.map(({ id, name }) => ({ id, label: name, sortKey: name.toLowerCase() }));
    const dialogRef = this.dialog.open(LinkGroupModalComponent, {
      data: {
        companyId,
        siteGroups,
        siteType: this.GMB_SITE_TYPE,
        cancelBtnText: this.basicService.translate('cancel'),
        confirmBtnText: this.basicService.translate('yes'),
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      const { siteGroupId } = result;
      if (typeof siteGroupId !== 'undefined') {
        const selectedLocations = this.dataSource.filteredData.filter(location =>
          this.selectedLocationIds.has(location.id)
        );
        const { companyId } = this.$stateParams;

        let lastLocationConnected: number;

        const calls = selectedLocations.map(location => {
          const { id } = location;
          this.connections[id].status = ConnectionStatus.AWAITING_CONNECTION;
          const data = {
            location,
            request_id: this.$stateParams.requestId,
            site_group_id: siteGroupId || null,
          };
          return of(1).pipe(
            tap(() => (this.connections[id].status = ConnectionStatus.CONNECTING)),
            switchMap(() =>
              this.googleBusinessProfileService.connectLocation(companyId, data).pipe(
                catchError(response => {
                  const error = { ...response };
                  // The http code 500 means a generic issue from the async request. Do not display any details, but the common description.
                  if (response.status === 400 && response.error) {
                    this.errors[id] = response.error.error?.error_message_translation_key || 'bug/unexpected-error';
                  }
                  error.status = ConnectionStatus.ERROR;
                  return of(error);
                }),
                map(({ site_id }: { site_id: number }) => {
                  if (site_id) {
                    lastLocationConnected = site_id;
                    this.connections[id].status = ConnectionStatus.CONNECTED;
                  } else {
                    this.connections[id].status = ConnectionStatus.ERROR;
                    this.connections[id].checked = false;
                    return EMPTY;
                  }
                })
              )
            )
          );
        });

        from(calls)
          .pipe(
            concatMap(call => call),
            finalize(() => {
              this.selectedLocationIds = new Set();
              this.dataSource.filteredData.forEach(location => {
                const { id } = location;
                if (this.connections[id].status === ConnectionStatus.CONNECTED) {
                  location.is_connected = true;
                  this.connectedLocationsIds.add(id);
                }
              });
              this.basicService.showSpinner();
              this.googleBusinessProfileService
                .completeAuthorization(companyId, {
                  site_id: lastLocationConnected,
                })
                .pipe(
                  switchMap(() => this.sitesService.refreshResolvers()),
                  finalize(() => this.basicService.hideSpinner())
                )
                .subscribe();
            })
          )
          .pipe(finalize(() => (this.loading = false)))
          .subscribe(() => {});
      } else {
        this.loading = false;
      }
    });
  }

  cancel() {
    this.accountsService.redirectToAccountsPage(this.$state);
  }

  showErrors(id: number) {
    this.selectedError = this.errors[id];
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      panelClass: 'google-business-profile-locations-modal',
      data: {
        title: this.basicService.translate('add-account/gmb/locations/status/error/title'),
        description: this.basicService.translate('add-account/gmb/locations/status/error/description'),
        templateRef: this.selectedError ? this.errorDetailTemplateRef : null,
        confirmBtnText: this.basicService.translate('ok'),
      },
    });
    dialogRef.afterClosed().subscribe();
  }

  clearSearch() {
    this.filtersGroup.get('search').reset('');
  }
}
